import PrintStateOfOutput from "./PrintStateOfOutput";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { selectState, setSearchPopover } from "../outputSlice";
import { useAppDispatch } from "hooks/hooks";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import { useNavigate } from "react-router-dom";
import Popover from "components/Popover/Popover";
import SearchPopover from "./SearchPopover";
import { useSelector } from "react-redux";
import { IOutputListItemSearch } from "Dashboard/scenes/Stock/domain/IOutputListItemSearch";
import {
  Control,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { SearchType } from "Dashboard/domain/SearchType";
import { useLocalStorage } from "hooks/LocalStorage";
import { usePagePrint } from "hooks/usePagePrint";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";

interface IProps {
  defaultGetOutputListItemsSearchParams: IOutputListItemSearch;
  setGetOutputListItemsSearchParams: Dispatch<SetStateAction<IOutputListItemSearch>>;
  registerSearch: UseFormRegister<IOutputListItemSearch>;
  handleSubmitSearch: UseFormHandleSubmit<IOutputListItemSearch>;
  resetSearch: UseFormReset<IOutputListItemSearch>;
  controlSearch: Control<IOutputListItemSearch, any>;
  setFocusSearch: UseFormSetFocus<IOutputListItemSearch>;
  getValuesSearch: UseFormGetValues<IOutputListItemSearch>;
  setValueSearch: UseFormSetValue<IOutputListItemSearch>;
};

const Toolbox = ({
  defaultGetOutputListItemsSearchParams,
  setGetOutputListItemsSearchParams,
  registerSearch,
  handleSubmitSearch,
  resetSearch,
  controlSearch,
  setFocusSearch,
  getValuesSearch,
  setValueSearch,
}: IProps) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const { searchPopover } = useSelector(selectState);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchType, setSearchType] = useState(SearchType.BASIC);
  const [storedSearchTerm, setStoredSearchTerm]
    = useLocalStorage("stockOutputSearchQuery", "");
  const [storedSearchType, setStoredSearchType]
    = useLocalStorage("stockOutputSearchType", SearchType.BASIC);
  const [storedSearchInputs, setStoredSearchInputs]
    = useLocalStorage("stockOutputSearchInputs", null);

  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch(), undefined, true);

  useEffect(() => {
    if (storedSearchType === "BASIC") {
      setGetOutputListItemsSearchParams({ searchQuery: storedSearchTerm });
    }
    if (storedSearchType === "ADVANCED") {
      storedSearchInputs.searchQuery = undefined;
      setGetOutputListItemsSearchParams(storedSearchInputs);
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  useEffect(() => {
    if (storedSearchTerm) {
      setValueSearch("searchQuery", storedSearchTerm);
    }
    if (storedSearchType) {
      setSearchType(storedSearchType);
    }
    if (storedSearchInputs) {
      Object.keys(storedSearchInputs).forEach((key: any) => {
        if (key !== "searchQuery") {
          setValueSearch(key, storedSearchInputs[key]);
        }
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  const handleNavigation = (path: string) => {
    const currentSearchTerm = getValuesSearch("searchQuery");
    const currentSearchInputs = getValuesSearch();
    setStoredSearchTerm(currentSearchTerm);
    setStoredSearchType(searchType);
    setStoredSearchInputs(currentSearchInputs);
    navigate(path);
  };

  const handlePrint = usePagePrint(componentRef, { margins: "0.5cm" });

  const handleResetSearch = () => {
    setGetOutputListItemsSearchParams(defaultGetOutputListItemsSearchParams);
    resetSearch();
  };

  return (
    <>
      <nav className="container">
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Back}
            tooltip="Lager - Ulaz"
            toolbox={true}
            onClick={() => handleNavigation('/stock')}
          />
        </div>
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Search}
            tooltip="Traži"
            toolbox={true}
            onClick={() => dispatch(setSearchPopover({ isOpen: true }))}
          />
          {!isSearchFormEmpty
            && (<ToolboxButton
              icon={ToolboxButtonIcon.CancelSearch}
              tiny={true}
              tooltip="Pokaži sve"
              onClick={() => handleResetSearch()}
            />)}
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampa"
            toolbox={true}
            onClick={handlePrint} />
        </div>
        {/* <!-- Nova verzija pretrage --> */}
        <div ref={componentRef} className="print-area">
          <PrintStateOfOutput />
        </div>
      </nav>
      <Popover
        isOpen={searchPopover.isOpen}
        onClose={() => dispatch(setSearchPopover({ isOpen: false }))}
        title="Pretraga"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 190 }}
      >
        <SearchPopover
          handleSubmit={handleSubmitSearch}
          register={registerSearch}
          control={controlSearch}
          getValues={getValuesSearch}
          reset={resetSearch}
          setFocus={setFocusSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          setValue={setValueSearch}
          setGetOutputListItemsSearchParams={setGetOutputListItemsSearchParams}
        />
      </Popover>
    </>
  );
};

export default Toolbox;
